import { writable, derived } from 'svelte/store';

// Selected organization ID (defaults to user's own ID). Single-org context used by WRITE/deploy/cash/SSE paths.
// The U5 org-SUBSET view filter for consolidated reads is held locally in Portfolio.svelte (a controlled
// OrgMultiSelect via bind:selected), not a shared store — the selection is page-local and resets per
// strategy/portfolio + mode, so a global store would only add a second source of truth.
export const selectedOrgId = writable(null);

// The signed-in user's own id (== their personal org's OrgId). Set once at initialization so the app
// can tell "my own org context" from "an assigned org's context" (TASK-20260830 AC 4).
export const ownUserId = writable(null);

// True when the selected org context is ANOTHER tenant's book. Org context is then READ-ONLY: the
// server 403s backtest mutations and 404s lifecycle control, so mutation affordances hide/disable
// with an explanation instead of surfacing raw errors. False while either store is unset (startup,
// logout) — an unscoped state must never read as a spurious lockout.
export const isCrossTenantOrgContext = derived(
  [selectedOrgId, ownUserId],
  ([$selectedOrgId, $ownUserId]) => !!$selectedOrgId && !!$ownUserId && $selectedOrgId !== $ownUserId
);

// User's role
export const userRole = writable(null);

// User's role name (Client, Trader, Admin)
export const userRoleName = derived(userRole, $userRole => $userRole?.role?.roleName || null);

// Available organizations for the user
export const availableOrgs = writable([]);

// Is user an admin
export const isAdmin = derived(userRoleName, $userRoleName => $userRoleName === 'Admin');

// Is user a trader or admin (can trade)
export const canTrade = derived(userRoleName, $userRoleName =>
  $userRoleName === 'Admin' || $userRoleName === 'Trader'
);

// Is user read-only by ROLE (Client). Not the org-context axis: for "may this user mutate in the
// CURRENT org context", compose with isCrossTenantOrgContext above — grabbing the wrong one compiles
// and demos fine, then fails only for the admin-in-assigned-org path. (Currently unconsumed.)
export const isReadOnly = derived(userRoleName, $userRoleName => $userRoleName === 'Client');

// Helper function to initialize org context
export async function initializeOrgContext(userId, fetchMyOrgs, fetchMyRole) {
  try {
    ownUserId.set(userId);

    // Fetch user's role
    const roleData = await fetchMyRole();
    userRole.set(roleData);

    // Fetch user's organizations
    const orgsData = await fetchMyOrgs();
    availableOrgs.set(orgsData || []);

    // ALWAYS default to user's own org (where orgId === userId)
    // This ensures we never use the previous user's org context
    if (orgsData && orgsData.length > 0) {
      const defaultOrg = orgsData.find(org => org.orgId === userId) || orgsData[0];
      selectedOrgId.set(defaultOrg.orgId);
    }
  } catch (error) {
    console.error('Failed to initialize org context:', error);
  }
}

// Helper to reset org context on logout
export function resetOrgContext() {
  selectedOrgId.set(null);
  ownUserId.set(null);
  userRole.set(null);
  availableOrgs.set([]);
}

// Helper to check if user has access to an org
export function hasAccessToOrg(orgId, orgs) {
  return orgs.some(org => org.orgId === orgId);
}
